import { createSelector } from 'reselect';
import i18n from 'i18next';
import { OrderAdditionalServiceGdsServiceServiceType } from '@websky/graphql';
import { CustomSeatComfort, CustomServiceClass } from '../types';
import { getState } from '../rootSelector';
import { getSeatMapAdditionalServices } from '../additionalServices/selectors';
import { getIsFreeSeat } from '../../lib/getIsFreeSeat';
import { getSelectedSegmentId } from '../selectedSegmentId/selectors';
export const getSeatMapState = createSelector(getState, state => state.seatMap);
export const getSeatMapSegments = createSelector(getSeatMapState, seatMap => seatMap.segments);
export const getSeatMapSegmentsСount = createSelector(getSeatMapSegments, segments => segments.length);
export const getSelectedSegment = createSelector(getSeatMapSegments, getSelectedSegmentId, (segments, selectedSegmentId) => {
    return segments.find(segment => { var _a; return ((_a = segment.segmentsInfo) === null || _a === void 0 ? void 0 : _a.id) === selectedSegmentId.toString(); });
});
export const getSelectedSegmentFilters = createSelector(getSelectedSegment, segment => {
    const restrictions = new Set();
    segment.decks.forEach(deck => deck.rows.forEach(row => row.seats.forEach(seat => {
        if (seat.restrictions) {
            seat.restrictions.forEach(restriction => restrictions.add(restriction));
        }
    })));
    return Array.from(restrictions);
});
export const getIsNextSegmentAvaliable = createSelector(getSelectedSegmentId, getSeatMapSegmentsСount, (selectedSegmentId, seatMapSegmentsСount) => selectedSegmentId < seatMapSegmentsСount - 1);
export const getSelectedSegmentRows = createSelector(getSelectedSegment, selectedSegment => { var _a, _b, _c; return (_c = (_b = (_a = selectedSegment === null || selectedSegment === void 0 ? void 0 : selectedSegment.decks) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.rows) !== null && _c !== void 0 ? _c : []; });
export const getSeatMapRestrictionsBySegmentId = createSelector(getSelectedSegmentRows, selectedSegmentRows => {
    const restrictionSet = new Set();
    selectedSegmentRows.forEach(row => {
        var _a;
        (_a = row.seats) === null || _a === void 0 ? void 0 : _a.forEach(seat => {
            var _a;
            if ((_a = seat.restrictions) === null || _a === void 0 ? void 0 : _a.length) {
                seat.restrictions.forEach(restriction => restrictionSet.add(restriction));
            }
        });
    });
    return [...restrictionSet];
});
export const getSeatMapServices = createSelector(getSeatMapAdditionalServices, seatMapAdditionalService => {
    var _a, _b;
    return (_b = (_a = seatMapAdditionalService === null || seatMapAdditionalService === void 0 ? void 0 : seatMapAdditionalService.gdsServices) === null || _a === void 0 ? void 0 : _a.services) === null || _b === void 0 ? void 0 : _b.filter(service => service.type === OrderAdditionalServiceGdsServiceServiceType.Seat);
});
export const getIsWideSeatMap = createSelector(getSelectedSegmentRows, rowsBySegmentId => {
    return rowsBySegmentId === null || rowsBySegmentId === void 0 ? void 0 : rowsBySegmentId.some(row => { var _a, _b, _c; return ((_c = (_b = (_a = row.seats) === null || _a === void 0 ? void 0 : _a.filter(seat => !(seat === null || seat === void 0 ? void 0 : seat.isAisle))) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 6; });
});
export const getSeatMapServicesBySelectedSegmentId = createSelector(getSeatMapServices, getSelectedSegmentId, (services, selectedSegmentId) => {
    return services.filter(service => { var _a; return (_a = service.allowedSegments) === null || _a === void 0 ? void 0 : _a.some(segment => segment === null || segment === void 0 ? void 0 : segment.some(id => id === selectedSegmentId.toString())); });
});
export const getServicesRfiscByClassAndComfort = createSelector(getSelectedSegmentRows, rowsBySegmentId => {
    // Seats by service class
    const seatsByServiceClassMap = new Map();
    rowsBySegmentId === null || rowsBySegmentId === void 0 ? void 0 : rowsBySegmentId.forEach(row => {
        var _a;
        (_a = row.seats) === null || _a === void 0 ? void 0 : _a.forEach(seat => {
            var _a, _b, _c;
            let serviceClass = (_b = (_a = seat.seatService) === null || _a === void 0 ? void 0 : _a.serviceClass) !== null && _b !== void 0 ? _b : null;
            if (!serviceClass && seat && getIsFreeSeat(seat)) {
                serviceClass = CustomServiceClass.Free;
            }
            if (serviceClass) {
                seatsByServiceClassMap.set(serviceClass, [...((_c = seatsByServiceClassMap.get(serviceClass)) !== null && _c !== void 0 ? _c : []), seat]);
            }
        });
    });
    // Seat rfisc by service class and comfort
    const seatRfiscByServiceClassAndComfort = new Map();
    seatsByServiceClassMap.forEach((seats, serviceClass) => {
        const seatRfiscByComfort = new Map();
        seats.forEach(seat => {
            var _a, _b, _c;
            let comfort = (_b = (_a = seat.seatService) === null || _a === void 0 ? void 0 : _a.comfort) !== null && _b !== void 0 ? _b : null;
            if (!comfort && getIsFreeSeat(seat)) {
                comfort = CustomSeatComfort.Free;
            }
            if (comfort) {
                seatRfiscByComfort.set(comfort, new Set([...((_c = seatRfiscByComfort.get(comfort)) !== null && _c !== void 0 ? _c : []), seat.rfisc]));
            }
        });
        seatRfiscByServiceClassAndComfort.set(serviceClass, seatRfiscByComfort);
    });
    return seatRfiscByServiceClassAndComfort;
});
export const getServicesByClassAndComfort = createSelector(getSeatMapServicesBySelectedSegmentId, servicesBySelectedSegment => {
    // Services by service class
    const servicesByServiceClass = new Map();
    servicesBySelectedSegment.forEach(service => {
        var _a, _b;
        const serviceClass = (_a = service.serviceClass) !== null && _a !== void 0 ? _a : null;
        servicesByServiceClass.set(serviceClass, [...((_b = servicesByServiceClass.get(serviceClass)) !== null && _b !== void 0 ? _b : []), service]);
    });
    // Services by services class and comfort
    const servicesByServiceClassAndComfort = new Map();
    servicesByServiceClass.forEach((servicesByServiceClass, serviceClass) => {
        const servicesByComfort = new Map();
        servicesByServiceClass.forEach(service => {
            var _a, _b;
            const comfort = (_a = service.comfort) !== null && _a !== void 0 ? _a : null;
            servicesByComfort.set(comfort, [...((_b = servicesByComfort.get(comfort)) !== null && _b !== void 0 ? _b : []), service]);
        });
        servicesByServiceClassAndComfort.set(serviceClass, servicesByComfort);
    });
    return servicesByServiceClassAndComfort;
});
export const getSeatServiceGroups = createSelector(getServicesRfiscByClassAndComfort, getServicesByClassAndComfort, (servicesRfiscByClassAndComfort, servicesByClassAndComfort) => {
    var _a, _b, _c, _d;
    const seatServices = [];
    servicesByClassAndComfort.forEach((servicesByComfort, serviceClass) => {
        servicesByComfort.forEach((services, comfort) => {
            var _a, _b;
            // Checking for the existence of seats for services
            if ((_a = servicesRfiscByClassAndComfort.get(serviceClass)) === null || _a === void 0 ? void 0 : _a.has(comfort)) {
                const seatsRfisc = (_b = servicesRfiscByClassAndComfort === null || servicesRfiscByClassAndComfort === void 0 ? void 0 : servicesRfiscByClassAndComfort.get(serviceClass)) === null || _b === void 0 ? void 0 : _b.get(comfort);
                let minPrice = null;
                let actualService = null;
                // Find min price and actual service
                services.forEach(service => {
                    var _a;
                    if (seatsRfisc === null || seatsRfisc === void 0 ? void 0 : seatsRfisc.has(service.rfisc)) {
                        if (((_a = service.price) === null || _a === void 0 ? void 0 : _a.amount) && (!minPrice || (minPrice === null || minPrice === void 0 ? void 0 : minPrice.amount) > service.price.amount)) {
                            minPrice = service.price;
                        }
                        if (!actualService || actualService.id < service.id) {
                            actualService = service;
                        }
                    }
                });
                if (actualService !== null) {
                    const { comfort: actualComfort, description } = actualService;
                    seatServices.push(Object.assign(Object.assign({}, actualService), { description: i18n.t(description, { ns: 'SeatMap' }), serviceClass: actualService.serviceClass, 
                        // надо поправить потом для показа нескольких иконок в SeatInfo
                        seats: [{ comfort: actualComfort }], price: minPrice, availableRfisc: [...(seatsRfisc !== null && seatsRfisc !== void 0 ? seatsRfisc : [])] }));
                }
            }
        });
    });
    // Adding a service of free seats
    if ((_a = servicesRfiscByClassAndComfort.get(CustomServiceClass.Free)) === null || _a === void 0 ? void 0 : _a.has(CustomSeatComfort.Free)) {
        seatServices.push({
            rfisc: '',
            name: i18n.t('Free seats', { ns: 'SeatMap' }),
            type: OrderAdditionalServiceGdsServiceServiceType.Seat,
            price: { amount: 0, currency: (_d = (_c = (_b = seatServices === null || seatServices === void 0 ? void 0 : seatServices[0]) === null || _b === void 0 ? void 0 : _b.price) === null || _c === void 0 ? void 0 : _c.currency) !== null && _d !== void 0 ? _d : null },
            serviceClass: CustomServiceClass.Free,
            seats: [{ comfort: CustomSeatComfort.Free }],
            availableRfisc: []
        });
    }
    return seatServices;
});
